import React from 'react';
import type {
    ImageCropUploadValue,
    ImageUploadPayload,
} from '@/components/imageCropUpload';
import { useResetOnKeyChange } from '@/utils';

export type BrandFormInitialValues = {
    name: string;
    image: ImageCropUploadValue | null;
};

/**
 * What the modal hands back when the user submits. `imageUrl` is the
 * *existing* URL on edits (or `null` when cleared); `imageUpload` is only set
 * when the user picked a fresh image that still needs to be uploaded.
 */
export type BrandFormSubmit = {
    name: string;
    imageUpload: ImageUploadPayload | null;
    imageUrl: string | null;
};

type UseBrandFormParams = {
    initialValues?: BrandFormInitialValues;
    isOpen: boolean;
    onSubmit: (input: BrandFormSubmit) => void;
};

const EMPTY_INITIAL: BrandFormInitialValues = {
    name: '',
    image: null,
};

/**
 * Form state for the Add / Edit Brand modal. Mirrors `useSupplierForm` minus
 * the contact fields: re-seed from `initialValues` on every open and close,
 * gate submit on the required name, and carry the cropper payload through so
 * the table hook can drive the upload.
 */
export const useBrandForm = ({
    initialValues,
    isOpen,
    onSubmit,
}: UseBrandFormParams) => {
    const initial = initialValues ?? EMPTY_INITIAL;

    const [image, setImage] = React.useState<ImageCropUploadValue | null>(
        initial.image,
    );
    const [name, setName] = React.useState(initial.name);

    // Re-seed every field from initialValues on each open/close transition —
    // see useCategoryForm. The modal is always mounted and the page opens Edit
    // and sets the record in one update, so seeding on open is what fills the
    // brand's values; seeding on close clears an Add draft. Keyed on `isOpen`
    // only, so a background refetch mid-edit doesn't clobber the user's typing.
    useResetOnKeyChange(isOpen, () => {
        setImage(initial.image);
        setName(initial.name);
    });

    const onNameChange = React.useCallback((value: string) => {
        setName(value);
    }, []);

    const onImageChange = React.useCallback(
        (value: ImageCropUploadValue | null) => {
            setImage(value);
        },
        [],
    );

    const trimmedName = name.trim();
    const isValid = trimmedName.length > 0;

    // We deliberately do NOT close the modal here — the parent's submit
    // handler owns the close decision (close on success, keep open on
    // failure so the inline `formError` is visible and the user can retry
    // without re-entering everything).
    const onSubmitClick = React.useCallback(() => {
        if (!isValid) {
            return;
        }
        onSubmit({
            name: trimmedName,
            imageUpload: image?.imageUpload ?? null,
            imageUrl: image?.imageUpload ? null : (image?.imageUrl ?? null),
        });
    }, [image, isValid, onSubmit, trimmedName]);

    return {
        image,
        isValid,
        name,
        onImageChange,
        onNameChange,
        onSubmit: onSubmitClick,
    };
};
